import type { ReactNode } from 'react';
import { MessageBar, MessageBarBody, Spinner, Text } from '@fluentui/react-components';
import type { PersonChecklist } from '@vigeo/shared';
import type { Person } from '@vigeo/shared';
import { BlockersPanel, ClearancePanel, NextActionCard, ReadinessSummary, statePresenters } from '@/features/readiness';
import { CandidateOverview } from '../CandidateOverview.js';
import { usePersonStyles } from './styles.js';

/** Step 1. Moved unchanged from `pages/PersonPage.tsx` (#462 §5). */
export interface OverviewStepProps {
  readonly person: Person;
  readonly isCandidate: boolean;
  readonly workflowPending: boolean;
  readonly workflowError: boolean;
  readonly hasOffer: boolean;
  readonly canEdit: boolean;
  readonly onDraftOffer: () => void;
  readonly onOpenOffer: () => void;
  readonly checklistNotice: ReactNode;
  readonly ready: PersonChecklist | undefined;
  readonly blockersHref: string;
}

export function OverviewStep({
  person, isCandidate, workflowPending, workflowError, hasOffer, canEdit,
  onDraftOffer, onOpenOffer, checklistNotice, ready, blockersHref,
}: OverviewStepProps) {
  const styles = usePersonStyles();
  if (isCandidate) {
    if (workflowPending) return <Spinner size="small" label="Loading" />;
    // Not "No offer yet": a failed read says nothing about whether an offer exists, and that
    // screen invites a second draft.
    if (workflowError) return <MessageBar intent="error"><MessageBarBody>Could not load this candidate’s offers. Try again in a moment.</MessageBarBody></MessageBar>;
    return <div className={styles.page}>
      {person.jobProfileKeys.length === 0 ? checklistNotice : null}
      <CandidateOverview person={person} hasOffer={hasOffer} canEdit={canEdit} onDraftOffer={onDraftOffer} onOpenOffer={onOpenOffer} />
    </div>;
  }
  return <div className={styles.page}>
    {checklistNotice}
    {ready ? <>
      <Text>{statePresenters[ready.state].meaning}</Text>
      <ClearancePanel checklist={ready} />
      <NextActionCard checklist={ready} canRecord={canEdit} />
      <ReadinessSummary checklist={ready} compact />
      <BlockersPanel checklist={ready} canRecord={canEdit} previewLimit={3} fullListHref={blockersHref} />
    </> : null}
  </div>;
}
